// Transitions between abutting clips on one track. A transition lives on the
// incoming clip (`Clip.transitionIn`) and is centered on the cut: it starts d/2
// before the cut and ends d/2 after it. Video: the outgoing clip keeps playing
// past its out-point (holding its last frame if the media runs out) while the
// incoming clip fades in over it. Audio: the two clips crossfade, using whatever
// extra media ("handles") each side has.
import { Clip, clipSpeed } from "./model";

export const DEFAULT_TRANSITION = 1; // seconds
const ADJACENT = 0.01; // s — a cut is "clean" if the clips meet within this

const live = (c: Clip) => c.takeActive !== false && !c.disabled;

/** The clip that ends exactly where `b` starts on the same track, if any. */
export function clipBefore(clips: Clip[], b: Clip): Clip | undefined {
  let best: Clip | undefined;
  for (const c of clips) {
    if (c === b || !live(c) || c.start >= b.start) continue;
    if (Math.abs(c.start + c.duration - b.start) < ADJACENT && (!best || c.start > best.start)) best = c;
  }
  return best;
}
/** The clip that starts exactly where `a` ends on the same track, if any. */
export function clipAfter(clips: Clip[], a: Clip): Clip | undefined {
  const end = a.start + a.duration;
  let best: Clip | undefined;
  for (const c of clips) {
    if (c === a || !live(c) || c.start <= a.start) continue;
    if (Math.abs(c.start - end) < ADJACENT && (!best || c.start < best.start)) best = c;
  }
  return best;
}

export interface TransitionWin { a: Clip; b: Clip; cut: number; d: number; t0: number; t1: number; }

/** Every active transition on a track. Duration is clamped so the window never
 *  runs past either clip. */
export function transitionsOf(clips: Clip[]): TransitionWin[] {
  const out: TransitionWin[] = [];
  for (const b of clips) {
    if (!b.transitionIn || b.transitionIn <= 0 || !live(b)) continue;
    const a = clipBefore(clips, b);
    if (!a) continue;
    const d = Math.min(b.transitionIn, a.duration, b.duration);
    if (d <= 0.001) continue;
    const cut = b.start;
    out.push({ a, b, cut, d, t0: cut - d / 2, t1: cut + d / 2 });
  }
  return out;
}

/** The transition covering timeline position `pos`, if any. */
export function transitionAt(clips: Clip[], pos: number): TransitionWin | undefined {
  if (!clips.some((c) => c.transitionIn)) return undefined; // fast path: most tracks have none
  return transitionsOf(clips).find((w) => pos >= w.t0 && pos < w.t1);
}

/** How an audio clip is actually scheduled once crossfades are applied: it may
 *  start earlier / end later than its box (using handles) with longer fades. */
export interface AudioPlan { start: number; end: number; offset: number; fadeIn: number; fadeOut: number; }

export function audioPlans(clips: Clip[], mediaDur: (c: Clip) => number): Map<string, AudioPlan> {
  const plans = new Map<string, AudioPlan>();
  for (const c of clips) plans.set(c.id, { start: c.start, end: c.start + c.duration, offset: c.offset, fadeIn: c.fadeIn, fadeOut: c.fadeOut });
  for (const w of transitionsOf(clips)) {
    const spA = clipSpeed(w.a), spB = clipSpeed(w.b);
    const postA = Math.max(0, (mediaDur(w.a) - (w.a.offset + w.a.duration * spA)) / spA); // media after A's out
    const preB = Math.max(0, w.b.offset / spB);                                          // media before B's in
    const left = Math.min(w.d / 2, preB), right = Math.min(w.d / 2, postA);
    const x = left + right;
    if (x < 0.005) continue; // no handles on either side → plain cut
    const pa = plans.get(w.a.id)!, pb = plans.get(w.b.id)!;
    pa.end = w.cut + right; pa.fadeOut = x;
    pb.start = w.cut - left; pb.offset = w.b.offset - left * spB; pb.fadeIn = x;
  }
  return plans;
}
